import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";

type ReactQueryApiv2ProviderProps = {
	children?: React.ReactNode;
	bearerToken: string | (() => string) | (() => Promise<string>);
	baseUrl?: string;
};

const Apiv2ReactQueryContext = React.createContext<{
	getToken: () => Promise<string>;
	baseUrl: string;
}>({ getToken: async () => "", baseUrl: "" });

export const shouldRetryApiv2Query = (failureCount: number, error: unknown) => {
	const status = error && typeof error === "object" && "status" in error ? error.status : undefined;

	if (typeof status === "number" && status >= 400 && status < 500) {
		return false;
	}

	return failureCount < 3;
};

export const ReactQueryApiv2Provider = ({ children, bearerToken, baseUrl = "" }: ReactQueryApiv2ProviderProps) => {
	const getToken = async () => {
		if (typeof bearerToken === "function") {
			return await bearerToken();
		}
		return bearerToken;
	};

	const [queryClient] = React.useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						retry: shouldRetryApiv2Query,
					},
				},
			})
	);

	return (
		<Apiv2ReactQueryContext.Provider value={{ getToken, baseUrl }}>
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		</Apiv2ReactQueryContext.Provider>
	);
};

export const useApiv2ReactQueryContext = () => {
	return React.useContext(Apiv2ReactQueryContext);
};
